import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from './api';
import { formatDate } from './helpers';

// Dimensions du tracé. Assez large pour signer confortablement, assez petit
// pour que l'image reste légère une fois enregistrée.
const LARGEUR = 560;
const HAUTEUR = 180;
const TAILLE_MAX = 200 * 1024;

/**
 * Signature personnelle.
 *
 * Deux façons de la déposer : la dessiner à la souris (ou au doigt sur une
 * tablette), ou importer la photo d'une signature manuscrite. Le fond reste
 * transparent pour que le tracé se pose proprement sur le document.
 *
 * À noter : c'est une reproduction visuelle, pas une signature électronique
 * au sens juridique. Ce qui fait foi, c'est la traçabilité — quel compte, à
 * quelle date — que l'application enregistre de son côté.
 */
function SignaturePad({ user }) {
  const canvasRef = useRef(null);
  const fichierRef = useRef(null);
  const dessine = useRef(false);
  const [signature, setSignature] = useState(null);
  const [majLe, setMajLe] = useState(null);
  const [vierge, setVierge] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const contexte = () => {
    const c = canvasRef.current;
    if (!c) return null;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1A2332';
    return ctx;
  };

  const charger = useCallback(() => {
    api
      .get(`/users/${user.id}/signature`)
      .then((res) => {
        setSignature(res.data.signature);
        setMajLe(res.data.signature_updated_at);
      })
      .catch(() => {});
  }, [user.id]);

  useEffect(() => { charger(); }, [charger]);

  // Coordonnées relatives au canvas, souris comme tactile.
  const position = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const source = e.touches ? e.touches[0] : e;
    return {
      x: ((source.clientX - rect.left) / rect.width) * LARGEUR,
      y: ((source.clientY - rect.top) / rect.height) * HAUTEUR,
    };
  };

  const commencer = (e) => {
    e.preventDefault();
    const ctx = contexte();
    if (!ctx) return;
    dessine.current = true;
    const { x, y } = position(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const tracer = (e) => {
    if (!dessine.current) return;
    e.preventDefault();
    const ctx = contexte();
    if (!ctx) return;
    const { x, y } = position(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setVierge(false);
  };

  const arreter = () => { dessine.current = false; };

  const effacer = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, LARGEUR, HAUTEUR);
    setVierge(true);
    setMessage('');
    setErreur('');
  };

  const enregistrerImage = (dataUrl) => {
    if (dataUrl.length > TAILLE_MAX) {
      setErreur('Image trop lourde : réduisez-la ou redessinez la signature.');
      return;
    }
    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .put(`/users/${user.id}/signature`, { signature: dataUrl })
      .then(() => { setMessage('Signature enregistrée.'); effacer(); charger(); })
      .catch((err) => setErreur(err.response?.data?.message || "L'enregistrement a échoué."))
      .finally(() => setEnvoi(false));
  };

  const enregistrerTrace = () => {
    if (vierge) return setErreur('Dessinez votre signature avant d’enregistrer.');
    enregistrerImage(canvasRef.current.toDataURL('image/png'));
  };

  // Import d'un scan : on redimensionne pour rester léger et on garde les
  // proportions, sinon une photo de téléphone ferait plusieurs mégaoctets.
  const importer = (e) => {
    const fichier = e.target.files?.[0];
    if (!fichier) return;
    setErreur(''); setMessage('');

    const lecteur = new FileReader();
    lecteur.onload = () => {
      const img = new Image();
      img.onload = () => {
        const echelle = Math.min(LARGEUR / img.width, HAUTEUR / img.height, 1);
        const l = Math.round(img.width * echelle);
        const h = Math.round(img.height * echelle);
        const hors = document.createElement('canvas');
        hors.width = l;
        hors.height = h;
        hors.getContext('2d').drawImage(img, 0, 0, l, h);
        enregistrerImage(hors.toDataURL('image/png'));
      };
      img.onerror = () => setErreur('Image illisible.');
      img.src = String(lecteur.result);
    };
    lecteur.onerror = () => setErreur('Lecture du fichier impossible.');
    lecteur.readAsDataURL(fichier);
  };

  const supprimer = () => {
    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .put(`/users/${user.id}/signature`, { signature: null })
      .then(() => { setMessage('Signature supprimée.'); charger(); })
      .catch((err) => setErreur(err.response?.data?.message || 'La suppression a échoué.'))
      .finally(() => setEnvoi(false));
  };

  return (
    <div>
      {erreur && <div className="error-message" style={{ marginBottom: 12 }}>{erreur}</div>}
      {message && <div className="success-message" style={{ marginBottom: 12 }}>{message}</div>}

      {signature && (
        <div className="signature-actuelle">
          <div className="signature-actuelle-label">
            Signature enregistrée{majLe ? ` le ${formatDate(majLe)}` : ''}
          </div>
          <img src={signature} alt="Ma signature" className="signature-image" />
          <button className="btn btn-sm btn-danger" onClick={supprimer} disabled={envoi}>
            Supprimer
          </button>
        </div>
      )}

      <p className="charte-note" style={{ marginBottom: 8 }}>
        {signature
          ? 'Pour la remplacer, redessinez ci-dessous ou importez une image.'
          : 'Dessinez votre signature ci-dessous, ou importez la photo d’une signature manuscrite.'}
      </p>

      <canvas
        ref={canvasRef}
        width={LARGEUR}
        height={HAUTEUR}
        className="signature-canvas"
        onMouseDown={commencer}
        onMouseMove={tracer}
        onMouseUp={arreter}
        onMouseLeave={arreter}
        onTouchStart={commencer}
        onTouchMove={tracer}
        onTouchEnd={arreter}
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" onClick={enregistrerTrace} disabled={envoi || vierge}>
          Enregistrer le tracé
        </button>
        <button className="btn btn-sm" onClick={effacer} disabled={vierge}>Effacer</button>
        <button className="btn btn-sm" onClick={() => fichierRef.current?.click()} disabled={envoi}>
          Importer une image
        </button>
        <input
          ref={fichierRef}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={importer}
        />
      </div>

      <p className="charte-note" style={{ marginTop: 10 }}>
        Votre signature s’apposera automatiquement sur les chartes et les documents
        que vous validez. Elle reste personnelle : personne d’autre ne peut la
        déposer ni la modifier, pas même la direction.
      </p>
    </div>
  );
}

export default SignaturePad;
